import { IPromise, IQService, ISCEService, ITimeoutService, noop } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IAssigneeIdMap, IAssignee } from "@gtmhub/assignees/models";
import {
  IMagicField,
  ISlackChannels,
  IAutomation,
  IAutomationAction,
  IAutomationTask,
  AutomationTriggerType,
  AutomationActionTypeByTargetType,
  ISlackChannel,
  AutomationMagicFieldFormatType,
  AutomationMagicFieldTypeName,
  IAutomationActionMetadata,
  IAutomationActionRecipients,
} from "../models/automation";
import { ITeamsIdMap } from "@gtmhub/teams";
import { ITag } from "@gtmhub/tags/models";
import { AutomationService } from "../services/automation";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { GoalService } from "@gtmhub/goals/goal.service";
import { localize } from "@gtmhub/localization";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { AssigneeSelectorModal, AssigneeActions, IAssigneesState, IAssigneesStoreState, formatSearchAssignees } from "@gtmhub/assignees";
import { GoalModalsService } from "@gtmhub/goals/goalModalsService";
import { IMetric, MetricService } from "@gtmhub/okrs/metrics";
import { INgRedux } from "@gtmhub/state-management";
import {
  checkIfActiveStatusConditionExists,
  filterDeletedRecipients,
  filterSupportedAutomationActions,
  magicFieldCopies,
  supportedAutomationActionsUI,
} from "../utils/utils";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { GoalsChangedEventArgs, goalSelected, metricSelected, OkrEvents } from "@gtmhub/okrs";
import { RolesActions, IRolesState, IRolesStoreState, IRole } from "@gtmhub/roles";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { SearchService } from "@gtmhub/shared/services/search.service";
import { SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search } from "@webapp/search/models/search.models";

type SanitizeHtmlConfig = {
  allowedTags?: string[];
  allowedAttributes?: Record<string, string[]>;
};

type SanitizeHtml = (input: string, config: SanitizeHtmlConfig) => string;

export type ParentType = "goal" | "metric";

type ActionAssigneeType = "user" | "team";

type AutomationActionCategory = "gtmhub" | "external";

type AutomationFieldType = "subject" | "content" | "slackContent";

export type SupportedAutomationActionUI<TriggerType extends AutomationTriggerType> = {
  displayName: string;
  actionType: AutomationActionTypeByTargetType[TriggerType];
};

export type SupportedAutomationActionsUI = {
  [TriggerType in AutomationTriggerType]: {
    [ActionCategory in AutomationActionCategory]: {
      actions: SupportedAutomationActionUI<TriggerType>[];
    };
  };
};

const actionsRequiringRoleSelection: AutomationActionTypeByTargetType[AutomationTriggerType][] = ["assign_role", "unassign_role"];

interface IAutomationActionOptionDetails {
  displayName: string;
  target: string;
  parentType?: ParentType;
}

export type AutomationActionIndicators = {
  saving?: IIndicator;
  loading?: IIndicator;
  loadingTags?: IIndicator;
};

export interface IAutomationActionScope extends IExtendedRootScopeService {
  isTagsModuleEnabled: boolean;
  isTaskModuleEnabled: boolean;
  assigneeSelected: {
    displayName: string;
    target: string;
    parentType?: ParentType;
  };
  selectAssignee(): void;
  showSelector: {
    slack: boolean;
    role: boolean;
  };
  button: string;
  assigneesIdMap: IAssigneeIdMap;
  assigneesState: IAssigneesState;
  magicFields: IMagicField[];
  recipients: IAutomationActionRecipients;
  supportedActionsUI: SupportedAutomationActionsUI;
  tags: ITag[];
  task: {
    name: string;
    description: string;
  };
  teams: ITeamsIdMap;
  email: {
    subject: string;
    content: string;
  };
  slackNotification: {
    content: string;
  };
  indicators: AutomationActionIndicators;
  select: { assignee: IAutomationActionOptionDetails[]; parent: IAutomationActionOptionDetails[] };
  chosenSlackChannel: {
    name: string;
    id: string;
  };
  selectedRole: IRole;
  parentSelected: IAutomationActionOptionDetails;
  currentAutomation: IAutomation<AutomationTriggerType>;
  selectedAction:
    | SupportedAutomationActionUI<AutomationTriggerType>
    | {
        displayName?: string;
        actionType?: AutomationActionTypeByTargetType[AutomationTriggerType];
      };
  slackChannels: ISlackChannels;
  accountRoles: IRole[];
  rolesState: IRolesState;
  editMode: boolean;
  sensitiveActionTypes: string[];
  magicFieldCopies: { [TypeName in AutomationMagicFieldTypeName]: string };
  filterSlackChannelByQuery(query: string): ISlackChannel[];
  filterRolesBySearchTerm(searchTerm: string): IRole[];
  chooseSlackChannel(channel: { id: string; name: string }): void;
  assignRole(role: IRole): void;
  selectTaskDetail(detail: "assignee" | "parent", input: { displayName: string; target: string; parentType: ParentType }): void;
  selectAction(action: { displayName: string; actionType: string }): void;
  addRecipient(recipient: IAssignee): void;
  save(): void;
  addOrRemoveTag(newTag: ITag): void;
  toggleCustomRecipient(selected: "creator" | "assignee" | "targetedUser"): void;
  getAssignees(value: string): void;
  insertMagicField(field: AutomationFieldType, newText: string): void;
  saveCaretPosition(field: AutomationFieldType): void;
  handleTagsLoading(loadingState: IPromise<ITag[]>): IPromise<void>;
  removeRecipient(recipientId: string, type: string): void;
  isActionValid(): boolean;
  getSlackButton();
  toggleSelector(key: "slack" | "role"): void;
  filterActions(action: SupportedAutomationActionUI<AutomationTriggerType>): SupportedAutomationActionUI<AutomationTriggerType> | undefined;
  isActionSupportedByTrigger(): boolean;
  canUserExecuteAction(): boolean;
  openParentSelector(): void;
  clearRoleSelection(): void;
}

interface IAutomationActionStateParams {
  automationId?: string;
  automationTaskId: string;
  actionId?: string;
}

const bindStateToScope = (state: IAssigneesStoreState & IRolesStoreState) => ({
  assigneesIdMap: state.assignees.map,
  assigneesState: state.assignees,
  accountRoles: state.roles.getRolesExcludeTeamRoles(),
  rolesState: state.roles,
});

const sanitizeConfig: SanitizeHtmlConfig = {
  allowedTags: ["h1", "h2", "h3", "h4", "h5", "h6", "p", "a", "ul", "ol", "li", "b", "i", "strong", "em", "br", "div"],
  allowedAttributes: {
    a: ["href"],
  },
};

export class AutomationActionCtrl {
  private sanitizeHtml: SanitizeHtml;
  private okrEvents: OkrEvents;

  public static $inject = [
    "$q",
    "$rootScope",
    "$sce",
    "$state",
    "$scope",
    "$timeout",
    "$stateParams",
    "AutomationService",
    "AccountResolverService",
    "GoalService",
    "UIErrorHandlingService",
    "AssigneeSelectorModal",
    "GoalModalsService",
    "AssigneeActions",
    "RolesActions",
    "$ngRedux",
    "SearchService",
    "FeatureToggleService",
    "MetricService",
  ];

  constructor(
    private $q: IQService,
    private $rootScope: IGtmhubRootScopeService,
    private $sce: ISCEService,
    private $state: StateService,
    private $scope: IAutomationActionScope,
    private $timeout: ITimeoutService,
    private $stateParams: IAutomationActionStateParams,
    private automationService: AutomationService,
    private accountResolverService: AccountResolverService,
    private goalService: GoalService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeSelectorModal: AssigneeSelectorModal,
    private goalModalsService: GoalModalsService,
    private assigneeActions: AssigneeActions,
    private rolesActions: RolesActions,
    private $ngRedux: INgRedux,
    private searchService: SearchService,
    private featureToggleService: FeatureToggleService,
    private metricService: MetricService
  ) {
    this.okrEvents = new OkrEvents(this.$scope);
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.$scope.indicators = {
      loading: { progress: true },
    };

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.rolesActions.getRolesIfMissing());

    this.getSanitizeHtml()
      .then(({ default: sanitizeHtml }) => {
        this.sanitizeHtml = sanitizeHtml;

        return this.$scope.watchUntil(() => this.$scope.assigneesState.isFetched && this.$scope.rolesState.isFetched);
      })
      .then(this.init);
  }

  private getSanitizeHtml(): IPromise<{ default: SanitizeHtml }> {
    const depDefer = this.$q.defer<{ default: SanitizeHtml }>();
    import(
      /* webpackChunkName: "sanitize-html" */
      "../../../../../wwwroot/lib/sanitize-html.min.js"
    ).then(depDefer.resolve, depDefer.reject);
    return depDefer.promise;
  }

  private init = (): void => {
    this.setDefaults();

    this.$scope.selectAssignee = this.selectAssignee;

    this.$scope.selectedAction = {};

    this.$scope.chosenSlackChannel = {
      id: "",
      name: "",
    };
    this.$scope.showSelector = {
      slack: false,
      role: false,
    };
    this.$scope.magicFieldCopies = magicFieldCopies;
    this.$scope.toggleSelector = this.toggleSelector;
    this.$scope.clearRoleSelection = this.clearRoleSelection;
    this.$scope.saveCaretPosition = this.saveCaretPosition;

    this.$scope.filterSlackChannelByQuery = this.filterSlackChannelByQuery;
    this.$scope.filterRolesBySearchTerm = this.filterRolesBySearchTerm;
    this.$scope.chooseSlackChannel = this.chooseSlackChannel;
    this.$scope.assignRole = this.assignRole;
    this.$scope.selectAction = this.selectAction;
    this.$scope.selectTaskDetail = this.selectTaskDetail;
    this.$scope.save = this.save;

    this.$scope.supportedActionsUI = supportedAutomationActionsUI(this.actionCopies);
    this.$scope.select = this.select;
    this.$scope.handleTagsLoading = this.handleTagsLoading;
    this.$scope.addOrRemoveTag = this.addOrRemoveTag;

    this.$scope.getAssignees = this.getAssignees;
    this.$scope.toggleCustomRecipient = this.toggleCustomRecipient;
    this.$scope.insertMagicField = this.insertMagicField;
    this.$scope.isActionValid = this.isActionValid;
    this.$scope.getSlackButton = this.getSlackButton;

    this.$scope.addRecipient = this.addRecipient;
    this.$scope.removeRecipient = this.removeRecipient;
    this.$scope.openParentSelector = this.openParentSelector;

    this.$scope.filterActions = this.filterActions;
    this.$scope.isActionSupportedByTrigger = this.isActionSupportedByTrigger;
    this.$scope.canUserExecuteAction = this.canUserExecuteAction;

    this.$q
      .all({
        currentAutomation: this.automationService.getAutomation(this.$stateParams.automationId),
        sensitiveActionTypes: this.automationService.getActionTypesWithSensitiveData(),
        isTagsModuleEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.ModuleTags),
        isTaskModuleEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.ModuleTasks),
      })
      .then(
        ({ currentAutomation, sensitiveActionTypes, isTagsModuleEnabled, isTaskModuleEnabled }) => {
          this.$scope.isTagsModuleEnabled = isTagsModuleEnabled;
          this.$scope.isTaskModuleEnabled = isTaskModuleEnabled;
          this.$scope.currentAutomation = currentAutomation;
          this.$scope.sensitiveActionTypes = sensitiveActionTypes.sensitiveDataExposingActionTypes;

          if (this.$stateParams.actionId) {
            this.fillEditData();
          }

          this.automationService.getAutomationActionTypes(this.$scope.currentAutomation.trigger.objectType).then((supportedActionsRes) => {
            this.$scope.supportedActionsUI = {
              ...this.$scope.supportedActionsUI,
              [this.$scope.currentAutomation.trigger.objectType]: {
                gtmhub: {
                  actions: filterSupportedAutomationActions<AutomationTriggerType>(
                    this.$scope.supportedActionsUI[this.$scope.currentAutomation.trigger.objectType].gtmhub.actions,
                    supportedActionsRes.supportedActionTypes
                  ),
                },
                external: {
                  actions: filterSupportedAutomationActions<AutomationTriggerType>(
                    this.$scope.supportedActionsUI[this.$scope.currentAutomation.trigger.objectType].external.actions,
                    supportedActionsRes.supportedActionTypes
                  ),
                },
              },
            };

            this.resetIndicator("loading");
          });
        },
        (error) => {
          this.$scope.indicators.loading = { error };
        }
      );
  };

  private emailSubjectSelectionRange?: Range;
  private emailContentSelectionRange?: Range;
  private slackContentSelectionRange?: Range;
  private actionCopies: Record<AutomationActionTypeByTargetType[AutomationTriggerType], string> = {
    tag: localize("add_tags"),
    email: localize("send_email"),
    create_task: localize("add_task_to"),
    delete_item: localize("delete"),
    send_slack_message: localize("send_slack_notification"),
    assign_role: localize("assign_role"),
    unassign_role: localize("unassign_role"),
  };
  private select: { assignee: IAutomationActionOptionDetails[]; parent: IAutomationActionOptionDetails[] } = {
    assignee: [
      {
        displayName: "The Objective creator",
        target: "creator",
        parentType: "goal",
      },
      {
        displayName: "The Objective owner",
        target: "owner",
        parentType: "goal",
      },
      {
        displayName: "The Key Result creator",
        target: "creator",
        parentType: "metric",
      },
      {
        displayName: "The Key Result owner",
        target: "owner",
        parentType: "metric",
      },
    ],
    parent: [
      {
        displayName: "Add to this Objective",
        target: "current",
        parentType: "goal",
      },
      {
        displayName: "Add to this Key Result",
        target: "current",
        parentType: "metric",
      },
      {
        displayName: "Choose a parent OKR",
        target: "parent_okr",
        parentType: "goal",
      },
      {
        displayName: "Do not attach",
        target: "",
        parentType: undefined,
      },
    ],
  };

  private openParentSelector = (): void => {
    if (this.$scope.currentAutomation.trigger.objectType === "metric") {
      this.goalModalsService.openGoalSelectModal({ invokedBy: "TaskRow" });
      return;
    }

    this.goalModalsService.openGoalSelectModal({});
  };

  private onParentSelected = (parentType: ParentType, parent: IMetric | IGoal) => {
    this.$scope.parentSelected = {
      displayName: parent.name,
      target: parent.id,
      parentType,
    };
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    if (args.reason === metricSelected) {
      this.onParentSelected("metric", args.detail.parent);
      return;
    }
    if (args.reason === goalSelected) {
      this.onParentSelected("goal", args.detail.goal);
    }
  };

  private filterActions = (action: SupportedAutomationActionUI<AutomationTriggerType>): SupportedAutomationActionUI<AutomationTriggerType> | undefined => {
    if (action.actionType === "tag" && !this.$scope.isTagsModuleEnabled) {
      return;
    }

    if (action.actionType === "create_task" && !this.$scope.isTaskModuleEnabled) {
      return;
    }

    const isExecutedOnUsers = this.$scope.currentAutomation.trigger.objectType === "user";
    if (isExecutedOnUsers) {
      const shouldAddItemToCollection = this.shouldDisplayUserAction(action);
      if (!shouldAddItemToCollection) return;
    }

    return action;
  };

  private shouldDisplayUserAction = (action: SupportedAutomationActionUI<AutomationTriggerType>): boolean => {
    const isAutomationTriggeredOnEvent = !this.$scope.currentAutomation.trigger.manual && !this.$scope.currentAutomation.trigger.scheduled;
    const hasActiveStatusCondition = checkIfActiveStatusConditionExists(this.$scope.currentAutomation.automationTask);
    if (action.actionType === "delete_item" && (isAutomationTriggeredOnEvent || !hasActiveStatusCondition)) {
      return false;
    }

    const isAutomationTriggeredOnDeletedOrDeactivated =
      this.$scope.currentAutomation.trigger.operation === "deactivated" || this.$scope.currentAutomation.trigger.operation === "deleted";
    if (action.actionType === "assign_role" && isAutomationTriggeredOnEvent && isAutomationTriggeredOnDeletedOrDeactivated) {
      return false;
    }

    if ((action.actionType === "assign_role" || action.actionType === "unassign_role") && !this.$rootScope.hasPermission("ManageConfiguration")) {
      return false;
    }

    return true;
  };

  private toggleSelector = (key: "slack" | "role"): void => {
    this.$scope.showSelector[key] = !this.$scope.showSelector[key];
  };

  private clearRoleSelection = (): void => {
    this.toggleSelector("role");
    this.$scope.selectedRole = null;
  };

  private getSlackButton = (): void => {
    this.automationService.getSlackButton().then(
      (result: string) => {
        this.$scope.button = this.$sce.trustAsHtml(result);
      },
      (rejection) => this.uiErrorHandlingService.handleModal(rejection)
    );
  };

  private selectAssignee = (): void => {
    const assigneeType = "user";

    this.assigneeSelectorModal.selectOne({ assigneeType }).then((assignee) => {
      this.$scope.assigneeSelected = {
        displayName: assignee.name,
        target: assignee.id,
      };
    }, noop);
  };

  private getAssignees = (searchTerm: string): IPromise<IAssignee[]> => {
    const searchRequests = this.searchService.getAssigneeSearchCollections("both", searchTerm);
    const searchBody = {
      searchTerm,
      operator: searchTerm ? SearchOperatorsEnum.matchPrefix : SearchOperatorsEnum.matchAny,
      searchRequests,
    };

    return this.searchService.getSomeSearchData<Search<"teams"> | Search<"users">>(searchBody).then(
      (response) => {
        return formatSearchAssignees(response.items);
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
        return [];
      }
    );
  };

  private toggleCustomRecipient = (selected: "creator" | "assignee" | "targetedUser"): void => {
    this.$scope.recipients[selected] = !this.$scope.recipients[selected];
  };

  private removeRecipient = (recipientId: string, type: ActionAssigneeType) => {
    if (type === "user") {
      this.$scope.recipients.users = this.$scope.recipients.users.filter((userId) => userId !== recipientId);
    }
    if (type === "team") {
      this.$scope.recipients.teams = this.$scope.recipients.teams.filter((teamId) => teamId !== recipientId);
    }
  };

  private addRecipient = (recipient: IAssignee): void => {
    if (recipient.type === "user" && this.$scope.recipients.users.indexOf(recipient.id) === -1) {
      this.$scope.recipients.users.push(recipient.id);
    }
    if (recipient.type === "team" && this.$scope.recipients.teams.indexOf(recipient.id) === -1) {
      this.$scope.recipients.teams.push(recipient.id);
    }

    this.$timeout(() => {
      const newRecipientEl = document.getElementById("newRecipient") as HTMLInputElement;
      if (newRecipientEl) {
        newRecipientEl.value = "";
      }
    }, 0);
  };

  private selectTaskDetail = (detail: "assignee" | "parent", input: { displayName: string; target: string; parentType: ParentType }): void => {
    if (detail === "assignee") {
      this.$scope.assigneeSelected = input;
    }
    if (detail === "parent") {
      this.$scope.parentSelected = input;
    }
  };

  private getMagicFields = (
    options: { formatType?: AutomationMagicFieldFormatType; targetType?: AutomationTriggerType } = { formatType: "email", targetType: "goal" }
  ): void => {
    this.automationService.getMagicFields(options).then(
      (result: IMagicField[]) => {
        this.$scope.magicFields = result;
      },
      (rejection) => this.uiErrorHandlingService.handleModal(rejection)
    );
  };

  private saveCaretPosition = (field: AutomationFieldType): void => {
    switch (field) {
      case "content":
        this.emailContentSelectionRange = window.getSelection().getRangeAt(0);
        break;
      case "subject":
        this.emailSubjectSelectionRange = window.getSelection().getRangeAt(0);
        break;
      case "slackContent":
        this.slackContentSelectionRange = window.getSelection().getRangeAt(0);
        break;
    }
  };

  private insertMagicField = (field: AutomationFieldType, newText: string): void => {
    switch (field) {
      case "content":
        if (this.emailContentSelectionRange) {
          this.emailContentSelectionRange.insertNode(document.createTextNode(newText));
        } else {
          this.$scope.email.content += newText;
        }
        break;
      case "subject":
        if (this.emailSubjectSelectionRange) {
          this.emailSubjectSelectionRange.insertNode(document.createTextNode(newText));
        } else {
          this.$scope.email.subject += newText;
        }
        break;
      case "slackContent":
        if (this.slackContentSelectionRange) {
          this.slackContentSelectionRange.insertNode(document.createTextNode(newText));
        } else {
          this.$scope.slackNotification.content += newText;
        }
        break;
    }
  };

  private automationStates: { rules: string } = {
    rules: "gtmhub.automation.rules",
  };

  private fillEditData = (): void => {
    const foundAction: IAutomationAction = this.automationService.getAutomationTask(
      this.$scope.currentAutomation,
      this.$stateParams.automationTaskId,
      (automationTask) => {
        return automationTask.actions.reduce((aggregated, action) => (action.id === this.$stateParams.actionId ? action : aggregated), {});
      }
    );

    this.$scope.editMode = true;

    if (!foundAction) return;

    this.$scope.selectedAction = {
      displayName: this.actionCopies[foundAction.actionType],
      actionType: foundAction.actionType,
    };

    // tags
    this.$scope.tags = foundAction.metadata?.tags?.map((title) => ({ title })) || [];

    // tasks
    if (foundAction.actionType === "create_task") {
      this.$scope.assigneeSelected = {
        displayName: this.selectedItem("assignee", foundAction),
        target: foundAction.metadata.assignee,
      };

      if (foundAction.metadata && foundAction.metadata.parent && foundAction.metadata.parent !== "current") {
        if (!foundAction.metadata.parentType || foundAction.metadata.parentType === "goal") {
          this.goalService.getGoal(foundAction.metadata.parent).then(
            (goal: IGoal) => {
              this.$scope.parentSelected = {
                displayName: goal.name,
                target: foundAction.metadata.parent,
                parentType: "goal",
              };
            },
            (rejection) => this.uiErrorHandlingService.handleModal(rejection)
          );
        }

        if (foundAction.metadata.parentType === "metric") {
          this.metricService.getMetric(foundAction.metadata.parent).then(
            (metric: IMetric) => {
              this.$scope.parentSelected = {
                displayName: metric.name,
                target: foundAction.metadata.parent,
                parentType: "metric",
              };
            },
            (rejection) => this.uiErrorHandlingService.handleModal(rejection)
          );
        }
      } else if (foundAction.metadata && foundAction.metadata.parent && foundAction.metadata.parent === "current") {
        const displayName = this.select.parent.find((p) => p.parentType === foundAction.metadata.parentType || !foundAction.metadata.parentType).displayName;

        this.$scope.parentSelected = {
          displayName,
          target: foundAction.metadata.parent,
          parentType: foundAction.metadata.parentType as ParentType,
        };
      }

      this.$scope.task = {
        name: foundAction.metadata.name,
        description: foundAction.metadata.description,
      };
    }

    // email
    if (foundAction.actionType === "email" && foundAction.metadata && foundAction.metadata.email) {
      this.getMagicFields({ targetType: this.$scope.currentAutomation.trigger.objectType });
      this.$scope.recipients = foundAction.metadata.recipients;

      this.$scope.recipients.users = filterDeletedRecipients(this.$scope.recipients.users, this.$scope.assigneesIdMap);
      this.$scope.recipients.teams = filterDeletedRecipients(this.$scope.recipients.teams, this.$scope.assigneesIdMap);

      this.$scope.email.content = foundAction.metadata.email;
      this.$scope.email.subject = foundAction.metadata.subject;
    }

    // slack notification
    if (foundAction.metadata && foundAction.metadata.message) {
      this.getMagicFields({ formatType: "slack", targetType: this.$scope.currentAutomation.trigger.objectType });
      this.getSlackChannels(foundAction.metadata.channel);

      this.$scope.slackNotification = {
        content: foundAction.metadata.message,
      };
    }

    if (actionsRequiringRoleSelection.includes(foundAction.actionType) && foundAction.metadata) {
      this.$scope.selectedRole = this.$scope.accountRoles.find((role) => role.id === foundAction.metadata.roleId);
    }
  };

  private selectedItem = (target: string, foundAction: IAutomationAction): string => {
    let targetDisplayName: string;
    this.$scope.select[target].forEach((item) => {
      if (item.target === foundAction.metadata[target] && item.parentType === foundAction.metadata.parentType) {
        targetDisplayName = item.displayName;
      }

      if (target !== "parent" && foundAction.metadata.assignee !== "creator" && foundAction.metadata.assignee !== "owner") {
        targetDisplayName = foundAction.metadata.assignee;
      }
    });

    return targetDisplayName;
  };

  private filterSlackChannelByQuery = (query: string): ISlackChannel[] => {
    if (!this.$scope.slackChannels) return [];

    return this.$scope.slackChannels.channels.filter((channel) => channel.name.toLocaleLowerCase().includes(query.toLocaleLowerCase()));
  };

  private filterRolesBySearchTerm = (searchTerm: string): IRole[] => {
    if (!this.$scope.accountRoles) return [];

    return this.$scope.accountRoles.filter((role) => role.name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()));
  };

  private chooseSlackChannel = (channel: { id: string; name: string }): void => {
    this.$scope.chosenSlackChannel = channel;
    this.$scope.showSelector.slack = false;
  };

  private assignRole = (role: IRole): void => {
    this.$scope.selectedRole = role;
    this.$scope.showSelector.role = false;
  };

  private setDefaults = (): void => {
    this.$scope.tags = [];

    this.$scope.recipients = {
      users: [],
      teams: [],
      creator: false,
      assignee: false,
      targetedUser: false,
    };

    this.$scope.email = {
      subject: "",
      content: "",
    };

    this.$scope.slackNotification = {
      content: "",
    };

    this.$scope.assigneeSelected = {
      displayName: "",
      target: "",
    };

    this.$scope.parentSelected = {
      displayName: "",
      target: "",
      parentType: null,
    };

    this.$scope.task = {
      name: "",
      description: "",
    };
  };

  private selectAction = (action: SupportedAutomationActionUI<AutomationTriggerType>) => {
    this.$scope.selectedAction = action;
    if (action.actionType === "email") {
      this.getMagicFields({ targetType: this.$scope.currentAutomation.trigger.objectType });
    }
    if (action.actionType === "send_slack_message") {
      this.getMagicFields({ formatType: "slack", targetType: this.$scope.currentAutomation.trigger.objectType });
      this.getSlackChannels();
    }

    this.setDefaults();
  };

  private handleTagsLoading = (loadingState: IPromise<ITag[]>): IPromise<void> => {
    this.$scope.indicators.loadingTags = { progress: true };
    return loadingState.then(
      () => this.resetIndicator("loadingTags"),
      (error) => {
        this.$scope.indicators.loadingTags = { error };
      }
    );
  };

  private addOrRemoveTag = (selectedTag: ITag): void => {
    if (this.$scope.tags.some((tag) => tag.title === selectedTag.title)) {
      this.$scope.tags = this.$scope.tags.filter((tag) => tag.title !== selectedTag.title);
    } else {
      this.$scope.tags.push(selectedTag);
    }
  };

  private save = (): void => {
    if (this.$stateParams.actionId) {
      this.editAction();
    } else {
      this.saveAction();
    }
  };

  private editAction = (): void => {
    const extractedActionMetadata = this.extractActionMetadata();

    const editableAction: IAutomationAction = {
      ...this.extractActionBase(),
      ...(extractedActionMetadata && { metadata: extractedActionMetadata }),
    };

    this.automationService.getAutomationTask(this.$scope.currentAutomation, this.$stateParams.automationTaskId, (automationTask: IAutomationTask) => {
      automationTask.actions.forEach((action, index) => {
        if (action.id === this.$stateParams.actionId) {
          automationTask.actions[index] = editableAction;
          this.updateAutomation(this.$scope.currentAutomation);
        }
      });
    });
  };

  private saveAction = (): void => {
    const extractedActionMetadata = this.extractActionMetadata();

    const action: IAutomationAction = {
      ...this.extractActionBase(),
      ...(extractedActionMetadata && { metadata: extractedActionMetadata }),
    };

    if (!this.$stateParams.automationTaskId) {
      this.$scope.currentAutomation.automationTask.actions.push(action);
    } else {
      const newAutomationTask = this.automationService.getAutomationTask(
        this.$scope.currentAutomation,
        this.$stateParams.automationTaskId,
        (automationTask) => automationTask
      );
      newAutomationTask.actions.push(action);
    }

    this.updateAutomation(this.$scope.currentAutomation);
  };

  private extractActionBase = (): IAutomationAction => {
    return {
      accountId: this.accountResolverService.getAccountId(),
      actionType: this.$scope.selectedAction.actionType,
      name: this.actionCopies[this.$scope.selectedAction.actionType],
    };
  };

  private extractActionMetadata = (): Partial<IAutomationActionMetadata> => {
    if (this.$scope.selectedAction.actionType === "tag") {
      return {
        tags: this.$scope.tags.map((tag) => tag.title),
      };
    }

    if (this.$scope.selectedAction.actionType === "email") {
      return {
        email: this.sanitizeHtml(document.getElementById("content").innerText, sanitizeConfig),
        subject: this.sanitizeHtml(document.getElementById("subject").innerText, sanitizeConfig),
        recipients: this.$scope.recipients,
      };
    }

    if (this.$scope.selectedAction.actionType === "create_task") {
      return {
        name: this.sanitizeHtml(document.getElementById("taskTitle").innerText, sanitizeConfig),
        description: this.sanitizeHtml(this.$scope.task.description, sanitizeConfig),
        assignee: this.$scope.assigneeSelected.target,
        parent: this.$scope.parentSelected.target,
        parentType: this.$scope.parentSelected.parentType,
      };
    }

    if (this.$scope.selectedAction.actionType === "send_slack_message") {
      return {
        workspace: this.$scope.slackChannels.workspace,
        channel: this.$scope.chosenSlackChannel.id,
        message: this.sanitizeHtml(document.getElementById("slackContent").innerText, sanitizeConfig),
      };
    }

    if (actionsRequiringRoleSelection.includes(this.$scope.selectedAction.actionType)) {
      return {
        roleId: this.$scope.selectedRole.id,
      };
    }
  };

  private isActionValid = (): boolean => {
    if (this.$scope.selectedAction.actionType === "tag" && this.$scope.tags.length > 0) {
      return true;
    }

    if (this.$scope.selectedAction.actionType === "create_task" && document.getElementById("taskTitle").innerText.length > 0 && this.$scope.assigneeSelected.target) {
      return true;
    }

    if (this.$scope.selectedAction.actionType === "delete_item") {
      return true;
    }

    if (
      this.$scope.selectedAction.actionType === "email" &&
      document.getElementById("content").innerText.length > 0 &&
      document.getElementById("subject").innerText.length > 0 &&
      (this.$scope.recipients.creator ||
        this.$scope.recipients.assignee ||
        this.$scope.recipients.targetedUser ||
        (this.$scope.recipients.users && this.$scope.recipients.users.length) ||
        (this.$scope.recipients.teams && this.$scope.recipients.teams.length))
    ) {
      return true;
    }

    if (
      this.$scope.selectedAction.actionType === "send_slack_message" &&
      document.getElementById("slackContent").innerText.length > 0 &&
      this.$scope.chosenSlackChannel.id &&
      this.$scope.slackChannels.workspace
    ) {
      return true;
    }

    if (actionsRequiringRoleSelection.includes(this.$scope.selectedAction.actionType) && this.$scope.selectedRole) {
      return true;
    }

    return false;
  };

  private isActionSupportedByTrigger = (): boolean => {
    const isExecutedOnUsers = this.$scope.currentAutomation.trigger.objectType === "user";
    if (isExecutedOnUsers) {
      const isAutomationTriggeredOnEvent = !this.$scope.currentAutomation.trigger.manual && !this.$scope.currentAutomation.trigger.scheduled;
      const hasActiveStatusCondition = checkIfActiveStatusConditionExists(this.$scope.currentAutomation.automationTask);

      if (this.$scope.selectedAction.actionType === "delete_item" && (isAutomationTriggeredOnEvent || !hasActiveStatusCondition)) {
        return false;
      }

      const isAutomationTriggeredOnDeletedOrDeactivated =
        this.$scope.currentAutomation.trigger.operation === "deactivated" || this.$scope.currentAutomation.trigger.operation === "deleted";
      if (this.$scope.selectedAction.actionType === "assign_role" && isAutomationTriggeredOnEvent && isAutomationTriggeredOnDeletedOrDeactivated) {
        return false;
      }
    }

    return true;
  };

  private canUserExecuteAction = (): boolean => {
    const isExecutedOnUsers = this.$scope.currentAutomation.trigger.objectType === "user";
    if (
      isExecutedOnUsers &&
      (this.$scope.selectedAction.actionType === "assign_role" || this.$scope.selectedAction.actionType === "unassign_role") &&
      !this.$rootScope.hasPermission("ManageConfiguration")
    ) {
      return false;
    }

    return true;
  };

  private updateAutomation = (automation: IAutomation<AutomationTriggerType>): void => {
    this.$scope.indicators.saving = { progress: true };

    this.automationService.updateAutomation(automation).then(
      () => {
        this.resetIndicator("saving");
        this.$rootScope.$broadcast("automationChanged");
        this.$state.go(this.automationStates.rules, { automationId: this.$stateParams.automationId }, { reload: false });
      },
      (error) => {
        this.$scope.indicators.saving = { error };
      }
    );
  };

  private getSlackChannels = (chosenChannelId?: string): void => {
    this.$scope.indicators.loading = { progress: true };
    this.automationService.getSlackChannels().then(
      (channels: ISlackChannels) => {
        this.$scope.slackChannels = channels;
        if (chosenChannelId) {
          this.$scope.chosenSlackChannel = this.$scope.slackChannels.channels.find((channel) => channel.id === chosenChannelId);
        }

        this.resetIndicator("loading");
      },
      (error) => {
        this.$scope.indicators.loading = { error };
      }
    );
  };

  private resetIndicator = (indicator: keyof AutomationActionIndicators): void => {
    delete this.$scope.indicators[indicator];
  };
}
